import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import * as elog from '@reverbdotcom/commons/src/elog';
import { connect } from '@reverbdotcom/commons/src/connect';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import Modal from '@reverbdotcom/commons/src/components/modal';
import { useHOCMutation, MutationFunction } from '@reverbdotcom/commons/src/useHOCMutation';
import { ExecutionResult } from 'graphql';
import { UpsertMyFavorite, CoreFindFavoriteProviderUpsertMyFavorite, DeleteMyFavorite } from '@reverbdotcom/commons/src/gql/graphql';
import { withRouter, WithRouterProps } from 'react-router';
import classNames from 'classnames';
import { RCButton, RCSwitch } from '@reverbdotcom/cadence/components';
import { DeleteMyFavoriteMutationFn, useDeleteMyFavorite, withDeleteMyFavoriteMutation } from './delete_my_favorite_mutation';

interface ExternalProps {
  isOpen: boolean;
  handleClose: Function;
  handleUpdate: Function;
  modalTitle: string;
  searchTitle: string;
  feedEnabled: boolean;
  emailEnabled: boolean;
  queryParams: string;
  subtitle?: string;
  handleMutationResult?: (result: ExecutionResult<UpsertMyFavorite.Mutation>) => void;
  withRemove?: boolean;
  handleRemove?: (result: ExecutionResult<DeleteMyFavorite.Mutation>) => void;
  favoriteId?: string;
  refetchQueryName?: string;
}

interface ApolloProps {
  upsertMyFavorite: MutationFunction<UpsertMyFavorite.Mutation, UpsertMyFavorite.Variables> | MutationFunction<CoreFindFavoriteProviderUpsertMyFavorite.Mutation, CoreFindFavoriteProviderUpsertMyFavorite.Variables>;
  deleteMyFavorite: DeleteMyFavoriteMutationFn;
}

type IProps = ExternalProps & ApolloProps & WithRouterProps;

export function FavoriteModal(props: IProps) {
  const [upsertFavorite] = useHOCMutation(props.upsertMyFavorite);
  const { deleteMyFavorite } = useDeleteMyFavorite(props.deleteMyFavorite, props.favoriteId, props.refetchQueryName);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await upsertFavorite({
        variables: {
          input: {
            queryParams: props.queryParams,
            feedEnabled: props.feedEnabled,
            emailEnabled: props.emailEnabled,
          },
        },
      });

      if (props.handleMutationResult) {
        props.handleMutationResult(result);
      }

      props.handleClose();
    } catch (e) {
      elog.error(
        e.message,
        { componentName: 'FavoriteSearchesModal' },
        e,
      );
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    try {
      const result = await deleteMyFavorite();
      props.handleClose();
      props.handleRemove(result);
    } catch (e) {
      elog.error(
        e.message,
        { componentName: 'FavoriteModal' },
        e,
      );
    }
  };

  const handleClose = (e) => {
    if (e) { e.preventDefault(); }
    props.handleClose();
  };

  const renderSubtitle = () => {
    return (
      props.searchTitle &&
        <div className="favorite-modal__header__shoppability-follow-title">
          {I18n.t('discovery.favorites.confirmationModal.firstToKnowSubtitle', { title: props.searchTitle })}
        </div>
    );
  };

  const headerClass = classNames(
    'favorite-modal__shoppability-header',
  );

  const filtersLabel = props.subtitle ? I18n.t('discovery.favorites.confirmationModal.filters') : I18n.t('discovery.favorites.confirmationModal.noFilters');

  return (
    <Modal
      isOpen={props.isOpen}
      subClass="feed"
      onRequestClose={handleClose}
    >
      <div className={headerClass}>
        <h1>{props.modalTitle}</h1>
        {renderSubtitle()}
      </div>

      <div className="favorite-modal__shoppability-subsection">
        <h1>{filtersLabel}</h1>
        <div className="favorite-modal__header__shoppability-text">
          {props.subtitle}
        </div>
      </div>
      <div className="favorite-modal__shoppability-subsection">
        <h1>{I18n.t('discovery.favorites.confirmationModal.savedSearchFeed')}</h1>

        <div className="favorite-modal__option">
          <RCSwitch
            checked={props.feedEnabled}
            id="add-to-feed-alert-toggle"
            label={I18n.t('discovery.favorites.confirmationModal.addMatchingItemsToFeed')}
            onChange={(e) => props.handleUpdate('feedEnabled', e.target.checked)}
          />
          {props.feedEnabled &&
            <RCSwitch
              checked={props.emailEnabled}
              id="add-to-email-alert-toggle"
              label={I18n.t('discovery.favorites.confirmationModal.addToYourDailyEmail')}
              onChange={e => props.handleUpdate('emailEnabled', e.target.checked)}
            />
          }
        </div>
      </div>

      <div className="favorite-modal__actions">
        <div className="favorite-modal__save-and-cancel-buttons">
          <RCButton
            id="submit-favorite"
            variant="filled"
            onClick={e => handleSubmit(e)}
          >
            {I18n.t('discovery.favorites.confirmationModal.save')}
          </RCButton>

          <div className="mobile-d-none">
            <RCButton
              variant="muted"
              onClick={() => props.handleClose()}
            >
              {I18n.t('discovery.favorites.confirmationModal.cancel')}
            </RCButton>
          </div>
        </div>

        {props.withRemove &&
          <RCButton
            variant="transparent"
            theme="danger"
            onClick={e => handleDelete(e)}
          >
            {I18n.t('discovery.favorites.confirmationModal.remove')}
          </RCButton>
        }
      </div>
    </Modal>
  );
}

/**
 * One mutation and connected component for the favorites hub (modifying settings for existing favorites)
 */
export const FAVORITES_HUB_UPSERT_FAVORITE_MUTATION = gql`
  mutation UpsertMyFavorite($input: Input_core_apimessages_UpsertMyFavoriteRequest) {
    upsertMyFavorite(input: $input) {
      favorite {
        id
        favorited
        emailEnabled
        feedEnabled
        searchableId
        searchableType
        queryParams
      }
    }
  }
`;

const withFavoritesHubUpsertFavoriteMutation =
  withGraphql<ExternalProps, UpsertMyFavorite.Mutation, UpsertMyFavorite.Variables>(
    FAVORITES_HUB_UPSERT_FAVORITE_MUTATION,
    {
      name: 'upsertMyFavorite',
    },
  );

export const FavoritesHubUpsertFavoriteModal = connect<ExternalProps>([
  withRouter,
  withFavoritesHubUpsertFavoriteMutation,
  withDeleteMyFavoriteMutation,
])(FavoriteModal);

/**
 * One mutation and connected component for the FindFavoriteProvider (saving a new favorite from marketplace pages or CSPs)
 */
export const FIND_FAVORITE_PROVIDER_UPSERT_FAVORITE_MUTATION = gql`
  mutation Core_FindFavoriteProvider_UpsertMyFavorite($input: Input_core_apimessages_UpsertMyFavoriteRequest) {
    upsertMyFavorite(input: $input) {
      favorite {
        id
        favorited
        emailEnabled
        feedEnabled
        searchableId
        searchableType
        queryParams
      }
    }
  }
`;

const withFindFavoriteProviderUpsertFavoriteMutation =
  withGraphql<ExternalProps, CoreFindFavoriteProviderUpsertMyFavorite.Mutation, CoreFindFavoriteProviderUpsertMyFavorite.Variables>(
    FIND_FAVORITE_PROVIDER_UPSERT_FAVORITE_MUTATION,
    {
      name: 'upsertMyFavorite',
    },
  );

export const FindFavoriteProviderUpsertFavoriteModal = connect<ExternalProps>([
  withRouter,
  withFindFavoriteProviderUpsertFavoriteMutation,
])(FavoriteModal);
